import type { GrammaticalCase, Gender, NounEntry, NounRequest, NounTable } from '../types';

/**
 * Deutsche Nominalflexion.
 *
 * Deckt bewusst nur das ab, was Spieltexte brauchen: bestimmter und unbestimmter
 * Artikel plus Substantiv in allen vier Kasus, Singular und Plural. Adjektive
 * werden nicht flektiert — stattdessen stehen feste Wendungen im Katalog.
 * Das haelt die Regelmenge klein und vermeidet die schwache/starke/gemischte
 * Adjektivdeklination, die ohne echten Parser kaum korrekt zu treffen ist.
 */

const DEFINITE: Record<GrammaticalCase, Record<Gender | 'pl', string>> = {
  nom: { m: 'der', f: 'die', n: 'das', pl: 'die' },
  acc: { m: 'den', f: 'die', n: 'das', pl: 'die' },
  dat: { m: 'dem', f: 'der', n: 'dem', pl: 'den' },
  gen: { m: 'des', f: 'der', n: 'des', pl: 'der' },
};

const INDEFINITE: Record<GrammaticalCase, Record<Gender, string>> = {
  nom: { m: 'ein', f: 'eine', n: 'ein' },
  acc: { m: 'einen', f: 'eine', n: 'ein' },
  dat: { m: 'einem', f: 'einer', n: 'einem' },
  gen: { m: 'eines', f: 'einer', n: 'eines' },
};

/**
 * Genitiv Singular fuer Maskulinum und Neutrum.
 * Zischlaute verlangen -es ("des Kompasses"), sonst genuegt -s ("des Wracks").
 * Mehrsilbige Woerter auf -en/-er/-el nehmen ebenfalls nur -s.
 */
function genitiveSingular(entry: NounEntry): string {
  if (entry.gen !== undefined) return entry.gen;

  const word = entry.sg;
  if (/(?:s|ß|x|z|sch)$/i.test(word)) return `${word}es`;
  return `${word}s`;
}

/**
 * Dativ Plural endet im Deutschen auf -n, sofern der Plural nicht ohnehin
 * darauf endet oder auf -s gebildet wird ("den Wracks", nicht "den Wracksn").
 */
function dativePlural(entry: NounEntry): string {
  if (entry.datPl !== undefined) return entry.datPl;

  const word = entry.pl;
  if (/(?:n|s)$/i.test(word)) return word;
  return `${word}n`;
}

function inflect(entry: NounEntry, req: NounRequest): string {
  if (req.number === 'pl') {
    return req.case === 'dat' ? dativePlural(entry) : entry.pl;
  }

  const gender = entry.gender ?? 'n';
  if (req.case === 'gen' && (gender === 'm' || gender === 'n')) {
    return genitiveSingular(entry);
  }

  return entry.sg;
}

function article(entry: NounEntry, req: NounRequest): string | null {
  if (req.article === 'none') return null;

  const gender = entry.gender ?? 'n';

  if (req.number === 'pl') {
    // Unbestimmter Artikel existiert im Plural nicht ("ein Wracks" gibt es nicht).
    if (req.article === 'indef') return null;
    return DEFINITE[req.case].pl;
  }

  return req.article === 'def' ? DEFINITE[req.case][gender] : INDEFINITE[req.case][gender];
}

/**
 * Verschmelzung von Praeposition und bestimmtem Artikel.
 * Nur im Singular und nur beim bestimmten Artikel — "von den Wracks" bleibt getrennt.
 */
const CONTRACTIONS: Readonly<Record<string, string>> = {
  'an dem': 'am',
  'an das': 'ans',
  'bei dem': 'beim',
  'durch das': 'durchs',
  'für das': 'fürs',
  'in dem': 'im',
  'in das': 'ins',
  'um das': 'ums',
  'von dem': 'vom',
  'vor dem': 'vorm',
  'zu dem': 'zum',
  'zu der': 'zur',
  'über das': 'übers',
  'auf das': 'aufs',
};

export function renderNounDe(req: NounRequest, nouns: NounTable): string {
  const entry = nouns[req.id];
  if (entry === undefined) {
    // Sichtbar statt still falsch — der Katalogtest schlaegt darauf an.
    return `⟨noun:${req.id}?⟩`;
  }

  const word = inflect(entry, req);
  const art = article(entry, req);
  const prep = req.prep?.trim();

  let phrase: string;
  if (prep === undefined || prep === '') {
    phrase = art === null ? word : `${art} ${word}`;
  } else if (art === null) {
    phrase = `${prep} ${word}`;
  } else {
    const contracted =
      req.number === 'sg' && req.article === 'def'
        ? CONTRACTIONS[`${prep.toLowerCase()} ${art}`]
        : undefined;
    phrase = contracted !== undefined ? `${contracted} ${word}` : `${prep} ${art} ${word}`;
  }

  return req.caps ? phrase.charAt(0).toUpperCase() + phrase.slice(1) : phrase;
}
